import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PlanSelected from "./PlanSelected";
import { Button } from "../arc/components/button/button";
import { Input } from "../arc/components/input/input";
import { Select } from "../arc/components/select/select";
import { PasswordStrength } from "../arc/components/password-strength/password-strength";
import { AXO_PASSWORD_RULES } from "../config/passwordRules";

const injectFont = () => {
  if (document.getElementById("dm-sans-font")) return;
  const link = document.createElement("link");
  link.id = "dm-sans-font";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap";
  document.head.appendChild(link);
};

const DM_SANS = "'DM Sans', system-ui, sans-serif";

const FIELDS = [
  {
    id: "companyName",
    label: "Company Name",
    type: "text",
    placeholder: "Acme Ltd",
  },
  {
    id: "name",
    label: "Full Name",
    type: "text",
    placeholder: "Michael Scott",
  },
  {
    id: "email",
    label: "Email Address",
    type: "email",
    placeholder: "you@company.com",
  },
  {
    id: "password",
    label: "Password",
    type: "password",
    placeholder: "••••••••",
  },
];

const MARKETS = [
  { code: "GB", label: "United Kingdom", comingSoon: false },
  { code: "AU", label: "Australia", comingSoon: true },
  { code: "ZA", label: "South Africa", comingSoon: true },
];

const SignUp = () => {
  useEffect(() => {
    injectFont();
  }, []);

  const [formData, setFormData] = useState({
    companyName: "",
    name: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPlanSelected, setShowPlanSelected] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [selectedMarkets, setSelectedMarkets] = useState(["GB"]);
  const [marketError, setMarketError] = useState("");

  const selectMarket = (code) => {
    const market = MARKETS.find((m) => m.code === code);
    if (!market || market.comingSoon) return;
    setMarketError("");
    setSelectedMarkets([code]);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (id === "password") {
      setPasswordError(
        value && value.length < 8
          ? "Password must be at least 8 characters"
          : "",
      );
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setPasswordError("");
    setMarketError("");
    if (formData.password.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }
    if (selectedMarkets.length === 0) {
      setMarketError("Select at least one country for your company data.");
      return;
    }
    setShowPlanSelected(true);
  };

  const handleFocus = (e) => {
    e.target.style.border = "1px solid rgba(131,148,171,0.8)";
    e.target.style.background = "rgba(255,255,255,0.1)";
    e.target.style.boxShadow = "0 0 0 3px rgba(92,113,143,0.2)";
  };

  const handleBlur = (e) => {
    e.target.style.border = "1px solid rgba(92,113,143,0.45)";
    e.target.style.background = "rgba(255,255,255,0.06)";
    e.target.style.boxShadow = "none";
    if (
      e.target.id === "password" &&
      e.target.value &&
      e.target.value.length < 8
    ) {
      setPasswordError("Password must be at least 8 characters");
    } else if (e.target.id === "password") {
      setPasswordError("");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: "#f1f2f4",
        fontFamily: DM_SANS,
        WebkitFontSmoothing: "antialiased",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 60%, rgba(12,44,84,0.08) 0%, transparent 45%), radial-gradient(circle at 85% 15%, rgba(92,113,143,0.1) 0%, transparent 45%)",
        }}
      />

      {showPlanSelected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <PlanSelected
            onClose={() => setShowPlanSelected(false)}
            userData={{
              companyName: formData.companyName,
              name: formData.name,
              email: formData.email,
              password: formData.password,
              markets: selectedMarkets,
            }}
          />
        </div>
      )}

      <div
        className="relative w-full max-w-md p-8 rounded-2xl auth-arc"
        style={{
          background:
            "linear-gradient(145deg, #0c2c54 0%, #0e3363 50%, #0a2d58 100%)",
          border: "1px solid rgba(131,148,171,0.2)",
          boxShadow:
            "0 24px 64px rgba(12,44,84,0.22), 0 4px 16px rgba(12,44,84,0.12), inset 0 1px 0 rgba(131,148,171,0.12)",
        }}
      >
        <div className="text-center mb-7">
          <div className="flex justify-center mb-4">
            <div
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "14px",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
                padding: "6px",
              }}
            >
              <img
                src="/assets/logo.png"
                alt="Axo Signal logo"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
          </div>
          <p
            style={{
              fontFamily: DM_SANS,
              color: "#ffffff",
              fontSize: "26px",
              fontWeight: "800",
              letterSpacing: "-0.03em",
              lineHeight: "1.2",
              marginTop: "8px",
            }}
          >
            Create your account
          </p>
        </div>

        {error && (
          <div
            className="mb-5 p-3 rounded-lg text-sm"
            style={{
              background: "rgba(239,68,68,0.15)",
              border: "1px solid rgba(239,68,68,0.35)",
              color: "#fca5a5",
              fontFamily: DM_SANS,
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {FIELDS.map(({ id, label, type, placeholder }) =>
            id === "password" ? (
              <PasswordStrength
                key={id}
                label={label}
                id={id}
                value={formData[id]}
                onChange={handleChange}
                rules={AXO_PASSWORD_RULES}
                error={passwordError}
                placeholder={placeholder}
                required
                disabled={loading}
              />
            ) : (
              <Input
                key={id}
                label={label}
                id={id}
                type={type}
                value={formData[id]}
                onChange={handleChange}
                placeholder={placeholder}
                required
                disabled={loading}
              />
            ),
          )}

          <div>
            <Select
              label="Data country"
              description="Australia and South Africa are coming soon."
              value={selectedMarkets[0] || ""}
              onValueChange={selectMarket}
              disabled={loading}
              options={MARKETS.map((m) => ({
                value: m.code,
                label: m.comingSoon ? `${m.label} (Coming soon)` : m.label,
                disabled: m.comingSoon,
              }))}
            />
            {marketError && (
              <p
                style={{
                  fontFamily: DM_SANS,
                  fontSize: "12px",
                  color: "#fca5a5",
                  marginTop: 6,
                }}
              >
                {marketError}
              </p>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            style={{
              width: "100%",
              fontFamily: DM_SANS,
              fontWeight: 600,
              marginTop: 24,
            }}
          >
            {loading ? "Creating account…" : "Next: Choose a Plan"}
          </Button>
        </form>

        <div
          className="mt-7 pt-6 text-center"
          style={{ borderTop: "1px solid rgba(92,113,143,0.3)" }}
        >
          <p
            style={{ fontFamily: DM_SANS, fontSize: "13px", color: "#ffffff" }}
          >
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-semibold"
              style={{ color: "#ffffff" }}
              onMouseEnter={(e) =>
                (e.target.style.color = "rgba(255,255,255,0.7)")
              }
              onMouseLeave={(e) => (e.target.style.color = "#ffffff")}
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
